<script setup>
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import PercentageCell from '@js/components/tables/cells/PercentageCell.vue';
import PriceCell from '@js/components/tables/cells/PriceCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { userSessionKey, type } = defineProps({
    userSessionKey: {
        type: String,
        required: false,
    },
    type: {
        type: String,
        required: false,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'name',
            header: () => 'Nom',
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.name,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'lastYearTurnoverExcludingTax',
            header: () => 'CA HT A-1',
            cell: (props) =>
                props.row.original.lastYear.turnoverExcludingTax
                    ? h(PriceCell, {
                          price: props.row.original.lastYear
                              .turnoverExcludingTax,
                          withDecimals: false,
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'lastYearApikomCost',
            header: () => 'Apikom A-1',
            cell: (props) =>
                props.row.original.apikomCostLabel
                    ? h(TextCell, {
                          text: props.row.original.apikomCostLabel,
                      })
                    : props.row.original.lastYear.apikomCost
                      ? h(PriceCell, {
                            price: props.row.original.lastYear.apikomCost,
                            withDecimals: false,
                        })
                      : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'lastYearApikomCostToTurnoverRatio',
            header: () => 'Apikom / CA % A-1',
            cell: (props) =>
                props.row.original.lastYear.apikomCostToTurnoverRatio
                    ? h(PercentageCell, {
                          percentage:
                              props.row.original.lastYear
                                  .apikomCostToTurnoverRatio,
                          withDecimals: true,
                          type:
                              props.row.original.currentYear
                                  .apikomCostToTurnoverRatio > 5
                                  ? 'danger'
                                  : 'success',
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'lastYearSeaAndSmaCostToTurnoverRatio',
            header: () => 'Pub / CA % A-1',
            cell: (props) =>
                props.row.original.lastYear.seaAndSmaCostToTurnoverRatio
                    ? h(PercentageCell, {
                          percentage:
                              props.row.original.lastYear
                                  .seaAndSmaCostToTurnoverRatio,
                          withDecimals: true,
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'currentYearTurnoverExcludingTax',
            header: () => 'CA HT A',
            cell: (props) =>
                props.row.original.currentYear.turnoverExcludingTax
                    ? h(PriceCell, {
                          price: props.row.original.currentYear
                              .turnoverExcludingTax,
                          withDecimals: false,
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'currentYearApikomCost',
            header: () => 'Apikom A',
            cell: (props) =>
                props.row.original.apikomCostLabel
                    ? h(TextCell, {
                          text: props.row.original.apikomCostLabel,
                      })
                    : props.row.original.currentYear.apikomCost
                      ? h(PriceCell, {
                            price: props.row.original.currentYear.apikomCost,
                            withDecimals: false,
                        })
                      : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'currentYearApikomCostToTurnoverRatio',
            header: () => 'Apikom / CA % A',
            cell: (props) =>
                props.row.original.currentYear.apikomCostToTurnoverRatio
                    ? h(PercentageCell, {
                          percentage:
                              props.row.original.currentYear
                                  .apikomCostToTurnoverRatio,
                          withDecimals: true,
                          type:
                              props.row.original.currentYear
                                  .apikomCostToTurnoverRatio > 5
                                  ? 'danger'
                                  : 'success',
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'currentYearSeaAndSmaCostToTurnoverRatio',
            header: () => 'Pub / CA % A',
            cell: (props) =>
                props.row.original.currentYear.seaAndSmaCostToTurnoverRatio
                    ? h(PercentageCell, {
                          percentage:
                              props.row.original.currentYear
                                  .seaAndSmaCostToTurnoverRatio,
                          withDecimals: true,
                      })
                    : null,
            enableSorting: false,
        }),
    ],
    url: route('front.projectManagement.apikom.postOpportunities'),
    userSessionKey,
    data: {
        type,
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :filters="filters"
        :available-filters="availableFilters"
        :set-filters="setFilters"
        :set-global-filter="setGlobalFilter"
        :loading="loading"
    />
</template>
